import { useState, useEffect } from 'react';
import { api } from '../services/api';

interface DNSProvider {
  id: string;
  name: string;
  type: string;
  status: 'active' | 'inactive' | 'error' | 'syncing';
  zones: number;
  lastSync: string;
  config: Record<string, any>;
  syncEnabled?: boolean;
  errorMessage?: string;
}

export const useProvidersData = () => {
  const [providers, setProviders] = useState<DNSProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncingProviders, setSyncingProviders] = useState<Set<string>>(new Set());

  const loadProviders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/v1/dns/providers');
      setProviders(response.data?.providers || []);
    } catch (error) {
      console.error('Failed to load providers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProvider = async (providerData: any) => {
    const typeMap: Record<string, string> = {
      cloudflare: 'cloudflare',
      route53: 'aws_route53',
      aws: 'aws_route53',
      gcp: 'gcp_cloud_dns',
      google: 'gcp_cloud_dns',
      azure: 'azure_dns',
      digitalocean: 'digitalocean',
      dnsimple: 'dnsimple',
      namecheap: 'namecheap',
      godaddy: 'godaddy',
      linode: 'linode',
      vultr: 'vultr',
      ovh: 'ovhcloud',
    };

    const payload = {
      name: providerData.name,
      provider_type: typeMap[(providerData.type || '').toLowerCase()] || providerData.type,
      credentials: providerData.credentials,
      settings: providerData.settings,
      is_active: true,
    };

    await api.post('/api/v1/dns/providers', payload);
    await loadProviders();
  };

  const handleUpdateProvider = async (providerId: string, providerData: any) => {
    const typeMap: Record<string, string> = {
      cloudflare: 'cloudflare',
      route53: 'aws_route53',
      aws: 'aws_route53',
      gcp: 'gcp_cloud_dns',
      google: 'gcp_cloud_dns',
      azure: 'azure_dns',
      digitalocean: 'digitalocean',
      dnsimple: 'dnsimple',
      namecheap: 'namecheap',
      godaddy: 'godaddy',
      linode: 'linode',
      vultr: 'vultr',
      ovh: 'ovhcloud',
    };

    const payload = {
      name: providerData.name,
      provider_type: typeMap[(providerData.type || '').toLowerCase()] || providerData.type,
      credentials: providerData.credentials,
      settings: providerData.settings,
      is_active: true,
    };

    await api.put(`/api/v1/dns/providers/${providerId}`, payload);
    await loadProviders();
  };

  const handleSyncProvider = async (providerId: string) => {
    setSyncingProviders((prev) => new Set(prev).add(providerId));
    try {
      await api.post(`/api/v1/dns/providers/${providerId}/sync`);
      await loadProviders();
    } catch (error) {
      console.error('Failed to sync provider:', error);
      throw error;
    } finally {
      setSyncingProviders((prev) => {
        const newSet = new Set(prev);
        newSet.delete(providerId);
        return newSet;
      });
    }
  };

  const handleDeleteProvider = async (id: string) => {
    if (!confirm('Are you sure you want to delete this provider?')) {
      return;
    }

    try {
      await api.delete(`/api/v1/dns/providers/${id}`);
      await loadProviders();
    } catch (error) {
      console.error('Failed to delete provider:', error);
      throw error;
    }
  };

  useEffect(() => {
    loadProviders();
  }, []);

  return {
    providers,
    loading,
    syncingProviders,
    loadProviders,
    handleAddProvider,
    handleUpdateProvider,
    handleSyncProvider,
    handleDeleteProvider,
  };
};